/**
 * Stok/ürün kartını etiket placeholder'larına çeviren adaptör.
 *
 * KDV UYARISI: `salePrice` alanının KDV dahil mi hariç mi olduğu ürün bazında
 * `vatStatus` alanına göre değişir (veritabanı default'u "excluded"). Bu yüzden
 * fiyat asla doğrudan basılmaz, önce buradaki hesaptan geçer. Aksi halde
 * ürünlerin bir kısmında etikete yanlış fiyat çıkar.
 */

import { InventoryItem } from '../../accounting/inventory/types/inventory.types';
import { PlaceholderData } from '../../../types/labelTemplate.types';
import { countryCodeToFlag, getCountryName } from '../../../utils/countries';
import { convertUTCToTurkey } from '../../../utils/dateUtils';

const CURRENCY_SYMBOLS: Record<string, string> = {
  TRY: '₺',
  TL: '₺',
  USD: '$',
  EUR: '€',
  GBP: '£',
};

export function getCurrencySymbol(currency?: string): string {
  if (!currency) return '₺';
  return CURRENCY_SYMBOLS[currency.toUpperCase()] ?? currency;
}

/** KDV dahil satış fiyatı */
export function getVatIncludedPrice(item: Pick<InventoryItem, 'salePrice' | 'vatRate' | 'vatStatus'>): number {
  const price = item.salePrice ?? 0;
  const rate = item.vatRate ?? 0;
  if (item.vatStatus === 'included') return price;
  return price * (1 + rate / 100);
}

/** KDV hariç satış fiyatı */
export function getVatExcludedPrice(item: Pick<InventoryItem, 'salePrice' | 'vatRate' | 'vatStatus'>): number {
  const price = item.salePrice ?? 0;
  const rate = item.vatRate ?? 0;
  if (item.vatStatus !== 'included') return price;
  return rate > 0 ? price / (1 + rate / 100) : price;
}

export function formatMoney(value: number): string {
  return new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);
}

/**
 * Fiyat değişim logunun tarihini etikete yazar.
 * Log tablosu TR duvar saatiyle duruyor (UtcNow+3); burası saati kaydırmıyor.
 */
export function formatDate(value?: string | null, withTime = false): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';

  const date = d.toLocaleDateString('tr-TR');
  if (!withTime) return date;
  return `${date} ${d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`;
}

/**
 * InventoryItem.CreatedAt gerçek UTC. Zone'suz gelince JS onu yerel sanıyor
 * (06:31 UTC → etikette 06:31). UTC kabul edip İstanbul'a çevirir.
 */
function formatUtcAsTurkey(value?: string | null, withTime = false): string {
  if (!value) return '';
  const d = convertUTCToTurkey(value);
  if (Number.isNaN(d.getTime())) return '';

  const opts: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Istanbul',
  };
  if (withTime) {
    opts.hour = '2-digit';
    opts.minute = '2-digit';
    opts.hour12 = false;
  }
  return new Intl.DateTimeFormat('tr-TR', opts).format(d);
}

export interface InventoryLabelContext {
  /** Kategori adları — InventoryItem yalnızca categoryId taşır */
  categoryNames?: Record<string, string>;
  brandNames?: Record<string, string>;
  companyName?: string;
}

export function inventoryItemToPlaceholders(
  item: InventoryItem,
  ctx?: InventoryLabelContext,
): PlaceholderData {
  const vatIncluded = getVatIncludedPrice(item);
  const vatExcluded = getVatExcludedPrice(item);
  const currency = item.saleCurrency ?? 'TRY';
  const unit = item.unit ?? 'Adet';

  return {
    urunAdi: item.name ?? '',
    stokKodu: item.stockCode ?? '',
    barkod: item.barcode ?? '',
    fiyat: formatMoney(item.salePrice ?? 0),
    fiyatKdvli: formatMoney(vatIncluded),
    fiyatKdvsiz: formatMoney(vatExcluded),
    paraBirimi: currency,
    paraSembol: getCurrencySymbol(currency),
    kdvOrani: item.vatRate !== undefined && item.vatRate !== null ? `%${item.vatRate}` : '',
    birim: unit,
    birimFiyat: `${formatMoney(vatIncluded)} ${getCurrencySymbol(currency)}/${unit}`,
    kategori: item.categoryId ? ctx?.categoryNames?.[item.categoryId] ?? '' : '',
    marka: item.brandId ? ctx?.brandNames?.[item.brandId] ?? '' : '',
    mensei: getCountryName(item.countryOfOrigin),
    menseiBayrak: countryCodeToFlag(item.countryOfOrigin),
    // Koşullu elemanlar bu anahtarı "isTrue" ile okur; yerli değilse boş
    // kalması gerekiyor ki "isNotEmpty" koşulu da çalışsın
    yerliMali: item.isDomesticProduct ? 'Evet' : '',
    // Değişim logu yoksa ürün kayıt tarihi (UTC → TR); log varsa o tarih korunur
    fiyatDegisimTarihi:
      formatDate(item.lastPriceChangeDate) || formatUtcAsTurkey(item.createdAt),
    fiyatDegisimTarihiSaatli:
      formatDate(item.lastPriceChangeDate, true) || formatUtcAsTurkey(item.createdAt, true),
    aciklama: item.description ?? '',
    firmaAdi: ctx?.companyName ?? '',
    tarih: new Date().toLocaleDateString('tr-TR'),
  };
}

/** Tasarımcı önizlemesinde gösterilecek örnek veri */
export const SAMPLE_INVENTORY_PLACEHOLDERS: PlaceholderData = {
  urunAdi: 'Kahve Çekirdeği 250 gr',
  stokKodu: 'STK-4471',
  barkod: '8690000123452',
  fiyat: '124,92',
  fiyatKdvli: '149,90',
  fiyatKdvsiz: '124,92',
  paraBirimi: 'TRY',
  paraSembol: '₺',
  kdvOrani: '%20',
  birim: 'Adet',
  birimFiyat: '149,90 ₺/Adet',
  kategori: 'İçecek',
  marka: 'Örnek Marka',
  mensei: 'Türkiye',
  menseiBayrak: '🇹🇷',
  yerliMali: 'Evet',
  fiyatDegisimTarihi: new Date().toLocaleDateString('tr-TR'),
  fiyatDegisimTarihiSaatli: `${new Date().toLocaleDateString('tr-TR')} 09:15`,
  aciklama: 'Örnek ürün açıklaması',
  firmaAdi: 'Örnek Firma A.Ş.',
  tarih: new Date().toLocaleDateString('tr-TR'),
  agirlik: '0,750',
};

export const SAMPLE_WMS_PLACEHOLDERS: PlaceholderData = {
  lokasyonKodu: 'A-01-03-B',
  lokasyonAdi: 'A Blok Raf 3',
  peron: 'A',
  raf: '03',
  depoAdi: 'Merkez Depo',
  firmaAdi: 'Örnek Firma A.Ş.',
  tarih: new Date().toLocaleDateString('tr-TR'),
};
